import React from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, LogIn } from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import { useCart } from "../lib/cart-context";
import { useAuth } from "../lib/auth-context";
import { useShop } from "../lib/shop-context";
import { imageUrl, formatEUR } from "../lib/api";

/**
 * Phase 35 hotfix — `/envio/carrito` full-page cart view.
 *
 * Before Phase 35, the cart existed exclusively as a header drop-down
 * (`CartDrawer`). A deep-link URL `/envio/carrito` did not resolve to any
 * route, so React Router's `:id` catch-all (installed by Phase 33 for
 * legacy shipment URLs) swallowed it and rendered the `LegacyEnvioIdRedirect`
 * loading placeholder. This page provides the missing literal route so
 * customers can bookmark, share and refresh the cart URL.
 *
 * Layout mirrors the drawer's contents (items list on the left, sticky
 * summary on the right) but expanded to full page width. Weight breakdown
 * and 3 €/kg shipping copy stay identical.
 */
export default function CartPage() {
  const { cart, updateItem, removeItem } = useCart();
  const { user } = useAuth();
  const shopCtx = useShop();
  const navigate = useNavigate();

  const percent = Math.min(
    100,
    Math.round((cart.subtotal / (cart.min_subtotal || 25)) * 100)
  );
  const remaining = Math.max(0, (cart.min_subtotal || 25) - cart.subtotal);
  const shopPrefix = shopCtx?.prefix || "/envio";

  const handleCheckout = () => {
    navigate(`${shopPrefix}/checkout`);
  };

  const startShopping = () => {
    navigate(`${shopPrefix}/`);
  };

  return (
    <div
      data-testid="cart-page"
      className="mx-auto max-w-7xl px-4 sm:px-6 py-8"
    >
      <header className="mb-6">
        <h1 className="font-black text-3xl sm:text-4xl tracking-tight">
          Tu carro
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          Revisa tu pedido antes de tramitarlo. El pedido mínimo es de{" "}
          {formatEUR(cart.min_subtotal ?? 25)}.
        </p>
      </header>

      {cart.items.length === 0 ? (
        <div
          data-testid="cart-page-empty-state"
          className="flex flex-col items-center justify-center gap-3 py-16 text-center rounded-2xl border border-neutral-200 bg-neutral-50"
        >
          <div className="h-16 w-16 rounded-full bg-neutral-100 grid place-items-center">
            <ShoppingBag className="h-7 w-7 text-neutral-400" />
          </div>
          <p className="font-semibold text-lg">Tu carro está vacío</p>
          <p className="text-sm text-neutral-500 max-w-xs">
            Añade productos para hacer tu compra. El pedido mínimo es de{" "}
            {formatEUR(cart.min_subtotal ?? 25)}.
          </p>
          <Button
            onClick={startShopping}
            className="mt-2 bg-[#00953B] hover:bg-[#007a30] rounded-full"
            data-testid="cart-page-start-shopping-btn"
          >
            Empezar a comprar
          </Button>
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-3">
          {/* LEFT — item list */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((it) => (
              <div
                key={it.product_id}
                data-testid={`cart-page-item-${it.product_id}`}
                className="flex gap-4 rounded-xl border border-neutral-200 bg-white p-3"
              >
                <div className="h-24 w-24 rounded-lg bg-neutral-50 border border-neutral-100 shrink-0 overflow-hidden">
                  {it.product?.image_url && (
                    <img
                      src={imageUrl(it.product.image_url)}
                      alt={it.product.name}
                      className="h-full w-full object-contain"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-neutral-500">
                    {it.product?.brand}
                  </div>
                  <div className="text-sm font-medium leading-tight line-clamp-2">
                    {it.product?.name}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {it.product?.unit_label} ·{" "}
                    {formatEUR(it.product?.price_eur || 0)}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="inline-flex items-center rounded-full border border-neutral-200 overflow-hidden">
                      <button
                        data-testid={`cart-page-decrement-${it.product_id}`}
                        className="h-8 w-8 grid place-items-center hover:bg-neutral-50"
                        onClick={() =>
                          updateItem(
                            it.product_id,
                            Math.max(0, it.quantity - 1)
                          )
                        }
                        aria-label="Disminuir"
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span
                        className="h-8 min-w-[2rem] px-2 grid place-items-center text-sm font-semibold"
                        data-testid={`cart-page-qty-${it.product_id}`}
                      >
                        {it.quantity}
                      </span>
                      <button
                        data-testid={`cart-page-increment-${it.product_id}`}
                        className="h-8 w-8 grid place-items-center hover:bg-neutral-50"
                        onClick={() =>
                          updateItem(it.product_id, it.quantity + 1)
                        }
                        aria-label="Aumentar"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="text-sm font-semibold">
                      {formatEUR(it.line_total)}
                    </div>
                    <button
                      data-testid={`cart-page-remove-${it.product_id}`}
                      onClick={() => removeItem(it.product_id)}
                      aria-label="Eliminar"
                      className="text-neutral-400 hover:text-red-600 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT — sticky summary */}
          <aside className="lg:col-span-1 lg:sticky lg:top-4 self-start rounded-xl border border-neutral-200 bg-neutral-50 p-5 space-y-4">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span
                  data-testid="cart-page-min-order-label"
                  className={
                    cart.min_order_reached
                      ? "text-[#00953B] font-semibold"
                      : "text-neutral-600"
                  }
                >
                  {cart.min_order_reached
                    ? "¡Pedido mínimo alcanzado!"
                    : `Añade ${formatEUR(remaining)} más para el pedido mínimo`}
                </span>
                <span className="text-neutral-500">
                  {formatEUR(cart.subtotal)} / {formatEUR(cart.min_subtotal)}
                </span>
              </div>
              <Progress
                value={percent}
                className="h-2 [&>div]:bg-[#00953B]"
                data-testid="cart-page-min-progress"
              />
            </div>

            <Separator />

            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600">Subtotal</span>
                <span data-testid="cart-page-subtotal">
                  {formatEUR(cart.subtotal)}
                </span>
              </div>
              {cart.shop === "envio" && cart.total_weight_kg > 0 && (
                <div className="flex justify-between text-xs text-neutral-500">
                  <span>Peso total</span>
                  <span data-testid="cart-page-total-weight">
                    {cart.total_weight_kg.toFixed(2)} kg
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-neutral-600 truncate">
                  {cart.shipping_label || "Envío"}
                </span>
                <span data-testid="cart-page-shipping">
                  {formatEUR(cart.shipping_cost)}
                </span>
              </div>
              <div className="flex justify-between font-bold text-base pt-1">
                <span>Total</span>
                <span data-testid="cart-page-total">
                  {formatEUR(cart.total)}
                </span>
              </div>
            </div>

            <Button
              data-testid="cart-page-checkout-btn"
              disabled={!cart.min_order_reached}
              onClick={handleCheckout}
              className="w-full h-12 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white font-semibold disabled:opacity-50"
            >
              Tramitar pedido
            </Button>

            <Button
              data-testid="cart-page-continue-shopping-btn"
              variant="outline"
              onClick={startShopping}
              className="w-full h-11 rounded-full"
            >
              Seguir comprando
            </Button>
          </aside>
        </div>
      )}
    </div>
  );
}
